import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { ChartCard } from '@practium/features/dashboard';
import styles from './BillingRevenueChart.module.css';
import { BillingTrend } from './BillingTrend';
import { BillingRevenueChartSkeleton } from './BillingRevenueChartSkeleton';

export interface BillingRevenuePoint {
	day: string;
	value: number;
}

interface BillingRevenueChartProps {
	title: string;
	subtitle: string;
	buttonText: string;
	onButtonClick: () => void;
	mainValue: string;
	trendValue: string;
	trendLabel: string;
	trendDirection: 'up' | 'down';
	data: BillingRevenuePoint[];
	emptyMessage?: string;
	isLoading?: boolean;
}

interface RevenueTooltipProps {
	active?: boolean;
	payload?: Array<{ value: number }>;
}

const RevenueChartTooltip = ({ active, payload }: RevenueTooltipProps) => {
	if (!active || !payload || payload.length === 0) return null;

	const formattedValue = `R$ ${payload[0].value.toLocaleString('pt-BR')}`;

	return (
		<div className={styles.tooltip}>
			<div className={styles.tooltipDot} />
			<span className={styles.tooltipLabel}>Faturamento</span>
			<span className={styles.tooltipValue}>{formattedValue}</span>
		</div>
	);
};

export const BillingRevenueChart = ({
	title,
	subtitle,
	buttonText,
	onButtonClick,
	mainValue,
	trendValue,
	trendLabel,
	trendDirection,
	data,
	emptyMessage,
	isLoading,
}: BillingRevenueChartProps) => {
	const isEmpty = !data || data.length === 0;

	return (
		<ChartCard
			title={title}
			subtitle={subtitle}
			buttonText={buttonText}
			onButtonClick={onButtonClick}
			isEmpty={isEmpty}
			emptyMessage={emptyMessage}
			isLoading={isLoading}
			skeleton={<BillingRevenueChartSkeleton />}
		>
			<div className={styles.info}>
				<span className={styles.mainValue}>{mainValue}</span>
				<BillingTrend value={trendValue} label={trendLabel} direction={trendDirection} />
			</div>

			<div className={styles.chartContainer}>
				<ResponsiveContainer width="100%" height="100%">
					<AreaChart data={data} margin={{ top: 0, right: 5, left: 5, bottom: 0 }}>
						<defs>
							<linearGradient id="billingRevenueGradient" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor="var(--primary-dark)" stopOpacity={0.7} />
								<stop offset="35%" stopColor="var(--gradient-chart-start)" stopOpacity={0.5} />
								<stop offset="85%" stopColor="var(--gradient-chart-end)" stopOpacity={0.1} />
								<stop offset="100%" stopColor="var(--gradient-chart-end)" stopOpacity={0} />
							</linearGradient>
						</defs>
						<XAxis
							dataKey="day"
							axisLine={false}
							tickLine={false}
							tick={{ fill: 'var(--text-secondary)', fontSize: '0.75rem' }}
							interval="preserveStartEnd"
						/>
						<Tooltip content={<RevenueChartTooltip />} cursor={false} />
						<Area
							type="monotone"
							dataKey="value"
							stroke="var(--primary)"
							strokeWidth={2}
							fillOpacity={1}
							fill="url(#billingRevenueGradient)"
							activeDot={{ r: 4, fill: 'var(--primary)' }}
						/>
					</AreaChart>
				</ResponsiveContainer>
			</div>
		</ChartCard>
	);
};
